import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of, finalize } from 'rxjs';
import { environment } from '../../../environments/environment';
import { API_ENDPOINTS } from '../../core/constants/api-endpoints';

export interface SubscribeNewsletterRequest {
  email: string;
}

export interface SubscribeNewsletterResult {
  subscriptionId: string;
  alreadySubscribed: boolean;
}

export interface NewsletterApiResponse {
  success: boolean;
  message: string | null;
  data: SubscribeNewsletterResult | null;
  errors: Record<string, string[]> | null;
  statusCode: number;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class NewsletterService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  // State signals
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal(false);
  readonly alreadySubscribed = signal(false);

  subscribe(email: string): Observable<NewsletterApiResponse | null> {
    const endpoint = API_ENDPOINTS.landing.newsletter;
    const url = `${this.baseUrl}${endpoint.path}`;

    this.isLoading.set(true);
    this.error.set(null);
    this.success.set(false);
    this.alreadySubscribed.set(false);

    const request: SubscribeNewsletterRequest = { email };

    return this.http.post<NewsletterApiResponse>(url, request).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.success.set(true);
          this.alreadySubscribed.set(response.data.alreadySubscribed);
        } else {
          this.error.set(response.message || 'Failed to subscribe');
        }
      }),
      catchError(err => {
        const errorMessage = this.extractErrorMessage(err);
        this.error.set(errorMessage);
        return of(null);
      }),
      finalize(() => {
        this.isLoading.set(false);
      })
    );
  }

  reset(): void {
    this.isLoading.set(false);
    this.error.set(null);
    this.success.set(false);
    this.alreadySubscribed.set(false);
  }

  private extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const err = error as Record<string, unknown>;

      // Check for validation errors
      if (err['error'] && typeof err['error'] === 'object') {
        const apiError = err['error'] as Record<string, unknown>;
        if (apiError['message'] && typeof apiError['message'] === 'string') {
          return apiError['message'];
        }
        if (apiError['errors'] && typeof apiError['errors'] === 'object') {
          const errors = apiError['errors'] as Record<string, string[]>;
          const firstKey = Object.keys(errors)[0];
          if (firstKey && errors[firstKey]?.[0]) {
            return errors[firstKey][0];
          }
        }
      }

      if (err['message'] && typeof err['message'] === 'string') {
        return err['message'];
      }
    }
    return 'An error occurred. Please try again.';
  }
}
